import { apiClient } from "@/services/api"
import { i18n } from "@/plugins/i18n"
import { isoDateToString } from "@/services/date"
import { isEntitled, knownEntitlement } from "@/services/entitlements"
import { FEATURE_SETS } from "@/services/featureSet"
import { ENTITY_ICONS } from "@/services/entityIcons"
import { hasPermission, hasPermissionsOfAll } from "@/services/permissionService"
import { PERMISSIONS } from "@/services/permission"

const authHeaders = { headers: { tenant: true, authorization: true } }

/** Every release the viewer may see, newest first, each with `seen` and `announce`. */
export const fetchReleaseNotes = () => apiClient.get("/releases", authHeaders)

// An empty/absent id list means "every release currently visible to me".
export const markReleaseNotesSeen = ids => apiClient.post("/releases/seen", { ids }, authHeaders)

// The Zeiterfassung card reaches anyone holding RECORD_OWN_TIME or VIEW_TIME_ENTRY_FOREIGN
// (ReleaseNoteCatalog). Whoever may review is the office, and is shown the page that is new to them;
// everybody else only records, and is shown Meine Zeiten.
const mayReviewTime = () => hasPermissionsOfAll([PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]).value

const may = permission => hasPermission(permission).value

// The purchase-price card reaches whoever sees an amount, in master data or only on a project
// (ReleaseNoteCatalog): the catalogue whose prices they see, else the projects where theirs are.
const purchasePriceRoute = () => {
  if (may(PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE)) return { name: "MachineOverview" }
  if (may(PERMISSIONS.VIEW_MATERIAL_PURCHASEPRICE)) return { name: "MaterialOverview" }
  return { name: "ProjectOverview" }
}

/**
 * How each highlight is presented, by the backend's highlight id (`ReleaseNoteCatalog.RELEASES`).
 * The wording lives in `plugins/i18n/{de,en}/releasenotes.js`; the server only sends ids.
 *
 * - `icon` — shown in place of the `image` when there is none, in the dialog and the changelog.
 * - `image` — optional still of the feature, from `public/releasenotes/`, captured from the real app
 *   by `marketing/capture-releasenotes.mjs`. A detail of the page rather than all of it, so the
 *   thumbnail still says something; a click opens it at full size (`LewReleaseNoteMedia`). May be a
 *   thunk, for a card whose `route` depends on the reader: the picture should be of where the button
 *   leads.
 * - `featureSet` — the one the feature's pages require (their route's `requiresFeatureSet`); absent
 *   means every plan has it. An announcement reaches every tenant, bought or not (the catalog cannot
 *   filter on a feature-set), so this decides two things: the card says whether the reader's plan
 *   includes the feature, and a tenant without it is sent to the add-on page instead of `route`.
 * - `route` — a thunk, since some destinations depend on the reader. May return null for a highlight
 *   with nowhere to go.
 */
const HIGHLIGHTS = {
  TIME_TRACKING: {
    icon: "mdi-clock-outline",
    image: () => (mayReviewTime() ? "/releasenotes/zeiten-pruefen.jpg" : "/releasenotes/zeiterfassung.jpg"),
    featureSet: FEATURE_SETS.TIME_TRACKING,
    route: () => ({ name: mayReviewTime() ? "TimeTrackingReview" : "TimeTrackingMy" }),
  },
  // The Leistungskatalog is a tab of each project, so the button leads to the projects.
  SERVICE_CATALOG: {
    icon: "mdi-format-list-numbered",
    image: "/releasenotes/leistungskatalog.jpg",
    route: () => ({ name: "ProjectOverview" }),
  },
  PURCHASE_PRICES: {
    icon: "mdi-cash-multiple",
    image: "/releasenotes/einkaufspreise.jpg",
    route: purchasePriceRoute,
  },
  // Also reaches whoever only writes reports (ReleaseNoteCatalog); without the machine list to open,
  // the card is read for its details and has no button.
  MACHINE_UNITS: {
    icon: ENTITY_ICONS.machine,
    image: "/releasenotes/maschinen.jpg",
    route: () => (may(PERMISSIONS.VIEW_MACHINE_GENERAL) ? { name: "MachineOverview" } : null),
  },
  REPORTS: {
    icon: ENTITY_ICONS.constructionreport,
    image: "/releasenotes/bautagesberichte.jpg",
    route: () => ({ name: "ProjectOverview" }),
  },
  BUDGET_HEALTH: {
    icon: "mdi-traffic-light-outline",
    image: "/releasenotes/budget-ampel.jpg",
    route: () => ({ name: "ProjectCosts" }),
  },
  PROJECT_LIST: {
    icon: ENTITY_ICONS.project,
    image: "/releasenotes/projektliste.jpg",
    route: () => ({ name: "ProjectOverview" }),
  },
  // Packets sit in three catalogues; the material one stands for them, the card's still shows it.
  PACKETS: {
    icon: ENTITY_ICONS.packet,
    image: "/releasenotes/pakete.jpg",
    route: () =>
      may(PERMISSIONS.VIEW_MATERIAL_GENERAL) || may(PERMISSIONS.VIEW_PACKET_GENERAL)
        ? { name: "MaterialOverview" }
        : null,
  },
  // Gated on FrontendFileBrowserService.BROWSABLE_WITH (ReleaseNoteCatalog), the same set as the route's
  // VIEW_FILE_PERMISSIONS, so whoever sees the card can open the page.
  FILE_BROWSER: {
    icon: "mdi-folder-multiple-outline",
    image: "/releasenotes/dateien.jpg",
    route: () => ({ name: "FileBrowser" }),
  },
  // A tab on every record kind rather than a page.
  ACTIVITY_LOG: {
    icon: "mdi-history",
    image: "/releasenotes/protokoll.jpg",
    route: () => null,
  },
  PERSONNEL_PLANNING: {
    icon: ENTITY_ICONS.personnelplanning,
    image: "/releasenotes/personalplanung.jpg",
    featureSet: FEATURE_SETS.PERSONNEL_PLANNING,
    route: () => ({ name: "PersonnelPlanning" }),
  },
  PROJECT_COSTS: {
    icon: "mdi-finance",
    image: "/releasenotes/nachkalkulation.jpg",
    route: () => ({ name: "ProjectCosts" }),
  },
}

const FALLBACK = { icon: "mdi-star-four-points-outline", image: null, route: () => null }

/**
 * "included" or "not_included" for a highlight's [featureSet] in the reader's plan, null while the
 * plan is not known yet. No feature-set means every plan has it.
 */
const planOf = featureSet => {
  if (!featureSet) return "included"
  const entitled = knownEntitlement(featureSet)
  if (entitled === null) return null
  return entitled ? "included" : "not_included"
}

/**
 * The presentation of one highlight. `hasOwn`, because the map is a plain object and an id of
 * "toString" or "constructor" would otherwise resolve to an inherited function. A highlight with
 * wording but no entry here gets a neutral icon, no link, and no statement about the plan.
 */
const highlightMeta = highlightId => {
  const known = Object.hasOwn(HIGHLIGHTS, highlightId)
  const meta = known ? HIGHLIGHTS[highlightId] : FALLBACK
  const image = typeof meta.image === "function" ? meta.image() : meta.image
  // `isEntitled`, not the plan: while the plan is unknown the guard decides, as for any other link.
  const locked = meta.featureSet && !isEntitled(meta.featureSet)
  return {
    icon: meta.icon ?? FALLBACK.icon,
    image: image ?? null,
    plan: known ? planOf(meta.featureSet) : null,
    route: locked ? { name: "FeatureLocked", params: { featureSet: meta.featureSet } } : meta.route(),
  }
}

const releaseKey = (releaseId, suffix) => `releasenotes.releases.${releaseId}.${suffix}`
const highlightKey = (releaseId, highlightId, part) => releaseKey(releaseId, `highlights.${highlightId}.${part}`)

/** The child ids under [key], in the bundle's order; none when there is nothing there. */
const idsUnder = key => Object.keys(i18n.global.tm(key) ?? {})

/**
 * What a highlight changes in detail: `…highlights.<id>.details.<SECTION>.items.<ITEM>.{term,text}`,
 * with an optional `…details.<SECTION>.title`, sections and items in the order the bundle lists them.
 * Only the ids are read off the bundle; every string still goes through `t`. A section without a title
 * is a plain list, and a highlight without `details` has none: the card shows only its two sentences.
 */
const detailsOf = (releaseId, highlightId) => {
  const { t, tm } = i18n.global
  const base = highlightKey(releaseId, highlightId, "details")
  return idsUnder(base).map(sectionId => {
    const section = `${base}.${sectionId}`
    return {
      key: sectionId,
      title: Object.hasOwn(tm(section) ?? {}, "title") ? t(`${section}.title`) : "",
      items: idsUnder(`${section}.items`).map(itemId => ({
        key: itemId,
        term: t(`${section}.items.${itemId}.term`),
        text: t(`${section}.items.${itemId}.text`),
      })),
    }
  })
}

/**
 * The highlights of [release] this client has wording for. One it has none for (older frontend, newer
 * backend — the two repositories do not deploy together) is left out rather than rendered as a raw key.
 */
const knownHighlights = release =>
  release.highlights.filter(highlight => i18n.global.te(highlightKey(release.id, highlight.id, "title")))

/**
 * Whether this client can show [release] at all. One it cannot must neither be announced nor marked
 * seen: acknowledging a dialog that showed nothing would hide the release for good, including from the
 * frontend release that brings its wording.
 */
export const isShowable = release => knownHighlights(release).length > 0

/**
 * [release] as both surfaces render it: date, title, and each known highlight with its details and
 * presentation.
 */
export const presentRelease = release => {
  const { t, te } = i18n.global
  const titleKey = releaseKey(release.id, "title")
  return {
    id: release.id,
    releasedOn: release.releasedOn,
    date: isoDateToString(release.releasedOn),
    title: te(titleKey) ? t(titleKey) : "",
    highlights: knownHighlights(release).map(highlight => ({
      id: highlight.id,
      key: `${release.id}:${highlight.id}`,
      title: t(highlightKey(release.id, highlight.id, "title")),
      text: t(highlightKey(release.id, highlight.id, "text")),
      details: detailsOf(release.id, highlight.id),
      ...highlightMeta(highlight.id),
    })),
  }
}
